import React, { useMemo } from 'react';
import SelectComponent from '@ui/components/Select/Select.component';
import ModalComponent from '@ui/components/Modal/Modal.component';
import ButtonComponent from '@ui/components/Button/Button.component';
import ButtonBarComponent from '@ui/components/Button-bar/Button-bar.component';
import ModalFormComponent from '@ui/components/Modal-form/Modal-form.component';
import { IValue } from '@ui/components/Select/IValue';
import { IProps } from './IProps';

const Component: React.FC<IProps> = ({
    lang,
    loading,
    serverError,
    timezone,
    language,
    languages,
    langMap,
    localTimezone,
    demoModeError,
    onTimezoneChange,
    onSaveClick,
    onClose,
    onInputClick,
    onLanguageChange,
    onBtnKeyDown,
    onSelectKeyDown,
}: IProps) => {
    const alertMessage = useMemo(() => {
        if (serverError) return 'serverError';
        else if (demoModeError) return 'demoModeError';
        else return '';
    }, [serverError, demoModeError]);

    const timezoneItems = useMemo(() => {
        const result: IValue[] = [];

        for (let i = 0; i < 27; i++) {
            const rawValue = i - 12;
            const value = rawValue * 60;
            const sign = value < 0 ? '-' : '+';
            const abs = Math.abs(rawValue);
            const hours = `${abs}`.padStart(2, '0');
            const label = `UTC${sign}${hours}:00`;
            const isLocalTz = localTimezone === value;
            const span = isLocalTz ? 'deviceTz' : undefined;
            result.push({ value, label, span });
        }

        return result;
    }, []);

    const langItems = useMemo(
        () => languages.map((value) => ({ value, label: langMap[value] })),
        [languages],
    );

    return (
        <ModalComponent
            className="settings"
            title="settings"
            alertText={alertMessage}
            lang={lang}
            loading={loading}
            onBtnKeyDown={onBtnKeyDown}
            onClose={onClose}
        >
            <ModalFormComponent>
                <SelectComponent
                    label="timezone"
                    name="timezone"
                    lang={lang}
                    value={timezone}
                    values={timezoneItems}
                    onChange={onTimezoneChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
                <SelectComponent
                    label="language"
                    name="language"
                    lang={lang}
                    value={language}
                    values={langItems}
                    onChange={onLanguageChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                    onKeyDown={onSelectKeyDown}
                />
            </ModalFormComponent>
            <ButtonBarComponent>
                <ButtonComponent
                    label="save"
                    lang={lang}
                    onClick={onSaveClick}
                    onKeyDown={onBtnKeyDown}
                />
                <ButtonComponent
                    color="error"
                    label="cancel"
                    lang={lang}
                    onClick={onClose}
                    onKeyDown={onBtnKeyDown}
                />
            </ButtonBarComponent>
        </ModalComponent>
    );
};

const SettingsComponent = React.memo(Component);

export default SettingsComponent;
